import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useParams, withRouter } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { axiosKK } from '../services/networkRequest';
import { BoxData, Product, Dimensions } from '../models/APIModels';
import { Formik } from 'formik';
import SubmitButton from '../components/SubmitButton';
import Title from '../components/Title';
import Box from '../components/Box';
import BoxesSummary from '../components/BoxesSummary';
import Button from '../components/Button';
import {
  HelpIcon,
  PackagesIcon,
} from '../components/icons/BoxesPageIcons';
import Prices from '../components/Prices';
import Loading from '../components/Loading';
import NavigationStatus from '../components/NavigationStatus';
import { Route } from '../utilities/Route';
import {
  SuitcaseMobileViewTable,
  SuitcaseTable,
} from '../components/SuitcaseTable';
import {
  DimensionWarningModal,
  WeightWarningModal,
} from '../components/SuitcaseWarningModal';
import { useViewport, Width } from '../utilities/Viewport';
import { SuitcaseCalculatorForm } from '../components/SuitcaseCalculator';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { useHistory } from 'react-router-dom';
import { PreselectedBoxesModal } from '../components/PreselectedBoxesModal';
import { HowItWorksModal } from '../components/HowItWorksModal';
import { useFlow, FlowType } from '../utilities/useFlow';

interface BoxForm {
  boxes: BoxData[];
}

interface BoxesApiResponse {
  message: string;
  products: BoxData[];
}

// interface BoxesProps extends RouteComponentProps<undefined, StaticContext, BoxesState> {}

// interface BoxesState {
//   storageRequired: boolean;
//   boxes: BoxData[];
//   suitcases?: Dimensions[];
//   suitcaseData?: Product,
// }

interface BoxFormErrors {
  boxes?: string;
}

interface BoxesParams {
  route: Route;
}
const getImageUrlForFlow = (box: BoxData, flow: FlowType): string => {
  const flowPrefix = '/img/keepr/';

  switch (box.name.toLowerCase()) {
    case 'small box ':
    case 'small box':
      return `${flowPrefix}small.png`;
    case 'medium box':
      return `${flowPrefix}medium.png`;
    case 'large box':
      return `${flowPrefix}large.png`;
    case 'extra large box':
      return `${flowPrefix}extra-large.png`;
    case 'suitcase box':
      return `${flowPrefix}suitcase-box.png`;
    case 'suitcase':
      return `${flowPrefix}suitcase.png`;
    case 'own box':
      return `${flowPrefix}own-box.png`;
    case 'packaging tape':
      return `${flowPrefix}packaging-tape.png`;
    case 'bubble wrap':
      return `${flowPrefix}bubble-wrap.png`;
    default:
      return box.imageUrl;
  }
};

function BoxesComponent(): JSX.Element {
  const [boxes, setBoxes] = useState<BoxData[]>([]);
  const { route } = useParams<BoxesParams>();

  const [suitcaseData, setSuitCaseData] = useState<Product>();

  const [suitcases, setSuitcases] = useState<Dimensions[]>([]);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);

  const isSuitcaseOrder = route === Route.suitcase;


  const flow = useFlow();

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);

    if (isSuitcaseOrder) {
      axiosKK
        .get<{ product: Product }>('/boxes/4')
        .then((response) => {
          if (!cancelled) setSuitCaseData(response.data.product);
        })
        .catch(console.error);
    } else {
      axiosKK
        .get<BoxesApiResponse>('/products/price')
        .then((response) => {
          if (!cancelled) {
            setBoxes(
              response.data.products.map((each) => {
                each.count = 0;
                each.imageUrl = getImageUrlForFlow(each, flow);
                return each;
              }),
            );
          }
        })
        .catch(console.error);
    }

    return () => { cancelled = true; };
  }, [isSuitcaseOrder, flow]);

  const isStorage = route.includes('storage');
  const pageTitle = isStorage
    ? `Box Storage - Choose Your Boxes | ${flow}`
    : `Box Shipping - Choose Your Boxes | ${flow}`;
  const pageDescription = isStorage
    ? `Select from our range of storage boxes. All-inclusive service with boxes, packing materials, collection, storage and delivery from ${flow}.`
    : `Select from our range of shipping boxes. Door-to-door box shipping service across the UK with packing materials included from ${flow}.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
      </Helmet>
      <NavigationStatus step={1} />

      {isSuitcaseOrder ? (
        <SuitcaseSelection
          suitcases={suitcases}
          suitcaseData={suitcaseData}
          updateSuitcases={(suitcase) => {
            setSuitcases((prevState) => [...prevState, suitcase]);
          }}
          removeItem={(idx) => {
            setSuitcases((prevState) => {
              const newState = prevState.slice();
              newState.splice(idx, 1);
              return newState;
            });
          }}
          removeAllItem={() => setSuitcases([])}
        />
      ) : (
        <StorageOrShippingSelection
          boxes={boxes}
          setBoxes={setBoxes}
          route={route}
          onShowHowItWorks={() => setShowHowItWorksModal(true)}
        />
      )}

      {/* How It Works Modal */}
      <HowItWorksModal
        show={showHowItWorksModal}
        close={() => setShowHowItWorksModal(false)}
        storageRequired={route.includes(Route.storage)}
      />
    </>
  );
}

interface SuitcaseSelectionProps {
  suitcases: Dimensions[];
  suitcaseData?: Product;
  updateSuitcases: (suitcase: Dimensions) => void;
  removeItem: (idx: number) => void;
  removeAllItem: () => void;
}

interface SuitcaseWarningModalProps {
  dimension: boolean;
  weight?: Dimensions;
}

function SuitcaseSelection({
  updateSuitcases,
  suitcases,
  removeAllItem,
  suitcaseData,
  ...props
}: SuitcaseSelectionProps) {
  const { width } = useViewport();

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: 'booking_step_suitcase' });
  }, [gtmEventTracking]);

  const history = useHistory();

  const [suitcaseWarningModal, setSuitcaseWarningModal] =
    useState<SuitcaseWarningModalProps>({
      dimension: false,
      weight: undefined,
    });

  return (
    <>
      <Title>Custom Size, tell us more...</Title>
      <p className="grey">
        We just need a few measurements to get you a price for your custom
        items.
      </p>
      <div className="suitcase-selection-component">
        <section className="items-to-send-calculator">
          <div className="title-section">
            <h2>Items to Send</h2>
          </div>
          <div className="calculator">
            <SuitcaseCalculatorForm
              updateSuitcases={updateSuitcases}
              openSuitcaseWeightWarningModal={(suitcase) =>
                setSuitcaseWarningModal((prevState) => ({
                  ...prevState,
                  weight: suitcase,
                }))
              }
              openSuitcaseWarningModal={() =>
                setSuitcaseWarningModal((prevState) => ({
                  ...prevState,
                  dimension: true,
                }))
              }
              removeAllItem={removeAllItem}
            />
          </div>
        </section>
        <section className="suitcase-table-container">
          {width < Width.Tablet ? (
            <SuitcaseMobileViewTable
              suitcases={suitcases}
              suitcaseShippingCost={suitcaseData?.shippingCost}
              {...props}
            />
          ) : (
            <SuitcaseTable
              suitcases={suitcases}
              suitcaseShippingCost={suitcaseData?.shippingCost}
              {...props}
            />
          )}
        </section>
      </div>

      <div className="suitcase-price-and-next">
        <Prices
          boxes={[]}
          storageRequired={false}
          suitcases={suitcases}
          suitcaseShippingCost={suitcaseData?.shippingCost}
        />

        <SubmitButton
          disabled={suitcases.length <= 0}
          onClick={() => {
            history.push(`/suitcase/collection_address`, {
              boxes: [],
              storageRequired: false,
              suitcases,
              suitcaseData,
            });
          }}
        >
          Next
          <img alt="" src="/img/right-arrow-circular-button-outline.png" />
        </SubmitButton>
      </div>

      {suitcaseWarningModal.dimension && (
        <DimensionWarningModal
          close={() =>
            setSuitcaseWarningModal((prevState) => ({
              ...prevState,
              dimension: false,
            }))
          }
        />
      )}
      {suitcaseWarningModal.weight && (
        <WeightWarningModal
          close={() =>
            setSuitcaseWarningModal((prevState) => ({
              ...prevState,
              weight: undefined,
            }))
          }
          mainButtonAction={() => {
            updateSuitcases(suitcaseWarningModal.weight!);
            setSuitcaseWarningModal((prevState) => ({
              ...prevState,
              weight: undefined,
            }));
          }}
        />
      )}
    </>
  );
}

interface StorageOrShippingSelectionProps {
  route: Route;
  boxes: BoxData[];
  setBoxes: Dispatch<SetStateAction<BoxData[]>>;
  onShowHowItWorks: () => void;
}

interface BoxGroup {
  id: string;
  title: string;
  items: BoxData[];
}

/**
 * Packing materials (bubble wrap, packaging tape) behave differently from the
 * containers: no shipping charge, no monthly storage rent. Grouping them apart
 * means the "no monthly charge" line on those two cards reads as a property of
 * the group rather than as an oddity, and it halves the scan cost of a
 * nine-card catalogue.
 *
 * Data-driven rather than name-driven, so a new material added in the admin
 * portal lands in the right group without a frontend release. Anything that
 * doesn't match falls through to the containers group — never hidden.
 */
function isPackingMaterial(box: BoxData): boolean {
  return !box.chargeShipping && box.storageCost === 0;
}

function groupBoxes(boxes: BoxData[]): BoxGroup[] {
  const sorted = [...boxes].sort((a, b) => a.sortOrder - b.sortOrder);
  const materials = sorted.filter(isPackingMaterial);
  const containers = sorted.filter((box) => !isPackingMaterial(box));

  // Only split when the split is meaningful — one empty group would leave a
  // section heading standing over nothing.
  if (!materials.length || !containers.length) {
    return [{ id: 'all', title: '', items: sorted }];
  }

  return [
    { id: 'containers', title: 'Boxes and cases', items: containers },
    { id: 'materials', title: 'Packing materials', items: materials },
  ];
}

function StorageOrShippingSelection({
  route,
  boxes,
  setBoxes,
  onShowHowItWorks,
}: StorageOrShippingSelectionProps) {
  const history = useHistory();
  const initialValues: BoxForm = {
    boxes: [],
  };

  const storageRequired = route.includes(Route.storage);
  const effect = storageRequired ? 'booking_step_storage' : 'booking_step_ship';

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: effect });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtmEventTracking]);

  const deliveryFeeString = process.env.REACT_APP_DELIVERY_FEE;

  if (!deliveryFeeString) {
    throw new Error('No delivery fee set');
  }

  const deliveryFee = parseInt(deliveryFeeString);
  const groups = groupBoxes(boxes);

  return (
    <div className="boxes-page">
      <header className="boxes-page__intro">
        <Title>{`What do you want to ${
          storageRequired ? 'store' : 'ship'
        }?`}</Title>
        {/*
          Two controls of different weight, so two treatments. "Suggested
          packages" is an accelerator that fills the order in one click — the
          fastest route for someone who does not know what they need — so it
          keeps the outline. "How it works" is help; it gets the quiet
          tertiary treatment rather than competing for the same attention.
          Both open a dialog, hence `aria-haspopup`.
        */}
        <div className="boxes-page__intro-actions">
          {storageRequired && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<PackagesIcon />}
              aria-haspopup="dialog"
              onClick={() => history.push(`/storage/boxes/packages`)}
            >
              Suggested packages
            </Button>
          )}
          <Button
            variant="tertiary"
            size="sm"
            leftIcon={<HelpIcon />}
            aria-haspopup="dialog"
            onClick={onShowHowItWorks}
          >
            How it works
          </Button>
        </div>
      </header>
      <Formik
        initialValues={initialValues}
        validateOnMount={true}
        validate={(values) => {
          const errors: BoxFormErrors = {};

          if (
            !values.boxes ||
            values.boxes.length === 0 ||
            values.boxes.reduce((acc, each) => each.count + acc, 0) === 0
          ) {
            errors.boxes = 'No boxes selected';
          }

          return errors;
        }}
        onSubmit={() =>
          history.push(`/${route}/collection_address`, {
            boxes: boxes.filter((value) => value.count),
            storageRequired,
          })
        }
        validateOnChange={true}
      >
        {({ handleSubmit, isSubmitting, setFieldValue }) => {
          // Single writer for both the catalogue state and the Formik mirror.
          // The previous implementation wrote `boxes[index] = box` in place —
          // which meant the sorted render order had to stay index-identical to
          // the state array, and left React re-rendering off a mutated
          // reference.
          const applyBoxes = (next: BoxData[]) => {
            setBoxes(next);
            setFieldValue('boxes', next, true);
          };

          return (
            <form className="boxes-page__form" onSubmit={handleSubmit}>
              <div className="boxes-page__layout">
                <div className="boxes-page__catalogue">
                  {boxes && boxes.length ? (
                    groups.map((group) => (
                      <section
                        className="boxes-group"
                        key={group.id}
                        aria-labelledby={
                          group.title ? `boxes-group-${group.id}` : undefined
                        }
                      >
                        {group.title && (
                          <h2
                            className="boxes-group__title"
                            id={`boxes-group-${group.id}`}
                          >
                            {group.title}
                          </h2>
                        )}
                        <ul className="boxes-grid">
                          {group.items.map((box) => (
                            <li className="boxes-grid__item" key={box.id}>
                              <Box
                                box={box}
                                storageRequired={storageRequired}
                                setBox={(updated) => {
                                  applyBoxes(
                                    boxes.map((each) =>
                                      each.id === updated.id ? updated : each,
                                    ),
                                  );
                                  return updated;
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))
                  ) : (
                    <Loading />
                  )}
                </div>
              </div>

              <BoxesSummary
                boxes={boxes}
                storageRequired={storageRequired}
                isSubmitting={isSubmitting}
                deliveryFee={deliveryFee}
              />

              {history.location.pathname === '/storage/boxes/packages' && (
                <PreselectedBoxesModal
                  boxes={boxes}
                  close={() => {
                    history.push('/storage/boxes');
                  }}
                  onSelectPredefinedBoxes={(preselectedBoxes) => {
                    applyBoxes(
                      boxes.map((box) => ({
                        ...box,
                        count: preselectedBoxes[box.id] ?? 0,
                      })),
                    );
                    history.push('/storage/boxes');
                  }}
                />
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default withRouter(BoxesComponent);
